import React, { useCallback, useMemo, useRef, useState } from "react";
import "./VisibilityReorderPicker.css";

const FILTER_ALL = "all";
const FILTER_VISIBLE = "visible";
const FILTER_HIDDEN = "hidden";

function normalizeItems(items) {
  return (items || []).filter((item) => item && item.id);
}

/**
 * Mobile-friendly single-list picker: toggle visibility with checkboxes, reorder with drag handles.
 * Replaces dual-list transfer boxes (Available / Visible + Add/Remove buttons).
 *
 * @param {object} props
 * @param {Array<{ id: string, label: string, fixed?: boolean }>} props.items
 * @param {string[]} props.selectedIds Ordered visible/selected ids
 * @param {(ids: string[]) => void} props.onChangeSelectedIds
 * @param {number} [props.minSelected=1] Minimum items that must stay selected
 * @param {boolean} [props.enableReorder=true] Show drag handles on selected rows
 * @param {string} [props.searchPlaceholder="Search…"]
 * @param {string} [props.emptyMessage="No items match your search."]
 */
export default function VisibilityReorderPicker({
  items,
  selectedIds,
  onChangeSelectedIds,
  minSelected = 1,
  enableReorder = true,
  searchPlaceholder = "Search…",
  emptyMessage = "No items match your search.",
}) {
  const [filter, setFilter] = useState(FILTER_ALL);
  const [search, setSearch] = useState("");
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const listRef = useRef(null);

  const itemById = useMemo(() => {
    const map = new Map();
    normalizeItems(items).forEach((item) => map.set(item.id, item));
    return map;
  }, [items]);

  const selectedOrdered = useMemo(
    () => (selectedIds || []).map((id) => itemById.get(id)).filter(Boolean),
    [selectedIds, itemById]
  );

  const selectedSet = useMemo(() => new Set(selectedOrdered.map((item) => item.id)), [selectedOrdered]);

  const hiddenItems = useMemo(() => {
    const hidden = normalizeItems(items).filter((item) => !selectedSet.has(item.id));
    return hidden.sort((a, b) => (a.label || a.id).localeCompare(b.label || b.id));
  }, [items, selectedSet]);

  const searchLower = search.trim().toLowerCase();

  const matchesSearch = useCallback(
    (item) => {
      if (!searchLower) return true;
      const label = String(item.label || item.id).toLowerCase();
      return label.includes(searchLower);
    },
    [searchLower]
  );

  const displayRows = useMemo(() => {
    let rows = [];
    if (filter === FILTER_VISIBLE) {
      rows = selectedOrdered;
    } else if (filter === FILTER_HIDDEN) {
      rows = hiddenItems;
    } else {
      rows = [...selectedOrdered, ...hiddenItems];
    }
    return rows.filter(matchesSearch);
  }, [filter, selectedOrdered, hiddenItems, matchesSearch]);

  const isFixed = (item) => Boolean(item.fixed);
  const isSelected = (id) => selectedSet.has(id);
  const canUncheck = (id) => {
    if (!isSelected(id)) return false;
    const item = itemById.get(id);
    if (item && isFixed(item)) return false;
    return selectedOrdered.length > minSelected;
  };

  const toggleItem = (id) => {
    const item = itemById.get(id);
    if (!item) return;
    if (isSelected(id)) {
      if (!canUncheck(id)) return;
      onChangeSelectedIds(selectedOrdered.filter((row) => row.id !== id).map((row) => row.id));
      return;
    }
    onChangeSelectedIds([...selectedOrdered.map((row) => row.id), id]);
  };

  const reorderIds = useCallback(
    (fromId, toId) => {
      if (!fromId || !toId || fromId === toId) return;
      const ids = selectedOrdered.map((row) => row.id);
      const fromIndex = ids.indexOf(fromId);
      const toIndex = ids.indexOf(toId);
      if (fromIndex < 0 || toIndex < 0) return;
      const next = ids.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      onChangeSelectedIds(next);
    },
    [onChangeSelectedIds, selectedOrdered]
  );

  const findRowIdFromPoint = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el || !listRef.current) return null;
    const row = el.closest("[data-reorder-id]");
    if (!row || !listRef.current.contains(row)) return null;
    return row.getAttribute("data-reorder-id");
  };

  const handleDragHandlePointerDown = (e, id) => {
    if (!enableReorder || !isSelected(id)) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDraggingId(id);
    setDropTargetId(id);
  };

  const handleDragPointerMove = (e) => {
    if (!draggingId) return;
    const targetId = findRowIdFromPoint(e.clientX, e.clientY);
    if (targetId && isSelected(targetId)) {
      setDropTargetId(targetId);
    }
  };

  const handleDragPointerUp = (e) => {
    if (!draggingId) return;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (dropTargetId && dropTargetId !== draggingId) {
      reorderIds(draggingId, dropTargetId);
    }
    setDraggingId(null);
    setDropTargetId(null);
  };

  const handleDragPointerCancel = () => {
    setDraggingId(null);
    setDropTargetId(null);
  };

  const visibleCount = selectedOrdered.length;
  const hiddenCount = hiddenItems.length;

  return (
    <div className="visibility-reorder-picker">
      <div className="visibility-reorder-picker__filters" role="tablist" aria-label="Column filter">
        <button
          type="button"
          role="tab"
          aria-selected={filter === FILTER_ALL}
          className={`visibility-reorder-picker__filter${filter === FILTER_ALL ? " is-active" : ""}`}
          onClick={() => setFilter(FILTER_ALL)}
        >
          All
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === FILTER_VISIBLE}
          className={`visibility-reorder-picker__filter${filter === FILTER_VISIBLE ? " is-active" : ""}`}
          onClick={() => setFilter(FILTER_VISIBLE)}
        >
          Visible ({visibleCount})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === FILTER_HIDDEN}
          className={`visibility-reorder-picker__filter${filter === FILTER_HIDDEN ? " is-active" : ""}`}
          onClick={() => setFilter(FILTER_HIDDEN)}
        >
          Hidden ({hiddenCount})
        </button>
      </div>

      <div className="visibility-reorder-picker__search-wrap">
        <input
          type="search"
          className="visibility-reorder-picker__search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
        />
      </div>

      {enableReorder && filter !== FILTER_HIDDEN ? (
        <p className="visibility-reorder-picker__hint">Press and hold the handle to reorder visible columns.</p>
      ) : null}

      <ul ref={listRef} className="visibility-reorder-picker__list" aria-label="Columns">
        {displayRows.length === 0 ? (
          <li className="visibility-reorder-picker__empty">{emptyMessage}</li>
        ) : (
          displayRows.map((item) => {
            const selected = isSelected(item.id);
            const fixed = isFixed(item);
            const checkboxDisabled = fixed || (selected && !canUncheck(item.id));
            const isDragging = draggingId === item.id;
            const isDropTarget = dropTargetId === item.id && draggingId && draggingId !== item.id;

            return (
              <li
                key={item.id}
                data-reorder-id={item.id}
                className={[
                  "visibility-reorder-picker__row",
                  selected ? "is-selected" : "",
                  isDragging ? "is-dragging" : "",
                  isDropTarget ? "is-drop-target" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <label className="visibility-reorder-picker__label">
                  <input
                    type="checkbox"
                    className="visibility-reorder-picker__checkbox"
                    checked={selected}
                    disabled={checkboxDisabled}
                    onChange={() => toggleItem(item.id)}
                  />
                  <span className="visibility-reorder-picker__label-text">{item.label || item.id}</span>
                  {fixed ? <span className="visibility-reorder-picker__fixed">(fixed)</span> : null}
                </label>
                {enableReorder && selected ? (
                  <button
                    type="button"
                    className="visibility-reorder-picker__drag-handle"
                    aria-label={`Reorder ${item.label || item.id}`}
                    onPointerDown={(e) => handleDragHandlePointerDown(e, item.id)}
                    onPointerMove={handleDragPointerMove}
                    onPointerUp={handleDragPointerUp}
                    onPointerCancel={handleDragPointerCancel}
                  >
                    <span aria-hidden="true">⋮⋮</span>
                  </button>
                ) : null}
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
